import { Button, Empty, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import FilterDropdown from '../../foundation/components/filter_dropdown/FilterDropdown';
import LinkedVariantsTooltip from '../probands_samples/LinkedVariantsTooltip';
import type { WorklistColumnFilters, WorklistSample } from './types';

type Props = {
  columnFilters: WorklistColumnFilters;
  currentPage: number;
  filterOptionsData?: Record<string, string[]>;
  handleClaim: (sample: WorklistSample) => Promise<void>;
  handleFilterChange: (columnKey: string, values: string[]) => void;
  handleFilterClear: (columnKey: string) => void;
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, unknown>,
    sorter: SorterResult<WorklistSample> | SorterResult<WorklistSample>[],
  ) => void;
  isClaiming: boolean;
  isFetching: boolean;
  pageSize: number;
  samples: WorklistSample[];
  totalResultCount: number;
};

const PoolTable = ({
  columnFilters,
  currentPage,
  filterOptionsData,
  handleClaim,
  handleFilterChange,
  handleFilterClear,
  handleTableChange,
  isClaiming,
  isFetching,
  pageSize,
  samples,
  totalResultCount,
}: Props) => {
  // Every cell is a real <Link> so Ctrl/Cmd+click, middle-click and the
  // browser's "Open link in new tab" context menu work like in Probands/Samples.
  const renderSampleLink = (value: React.ReactNode, record: WorklistSample) => (
    <Link to={`/samples/${record.id}`} className="worklist__cell-link">
      {value}
    </Link>
  );

  const columns: ColumnsType<WorklistSample> = useMemo(
    () => [
      {
        title: (
          <span>
            Sample id
            <FilterDropdown
              columnKey="sample_id"
              title="Sample ID"
              filterOptions={filterOptionsData?.sample_id || []}
              selectedValues={columnFilters.sample_id}
              onFilterChange={(values) =>
                handleFilterChange('sample_id', values)
              }
              onClear={() => handleFilterClear('sample_id')}
            />
          </span>
        ),
        dataIndex: 'sample_id',
        key: 'sample_id',
        sorter: true,
        render: (text, record) => renderSampleLink(text, record),
      },
      {
        title: (
          <span>
            Proband
            <FilterDropdown
              columnKey="subject_external_id"
              title="Proband"
              filterOptions={filterOptionsData?.subject_external_id || []}
              selectedValues={columnFilters.subject_external_id}
              onFilterChange={(values) =>
                handleFilterChange('subject_external_id', values)
              }
              onClear={() => handleFilterClear('subject_external_id')}
            />
          </span>
        ),
        dataIndex: 'subject_id',
        key: 'subject_external_id',
        sorter: true,
        render: (text, record) => renderSampleLink(text, record),
      },
      {
        title: (
          <span>
            Indication
            <FilterDropdown
              columnKey="indication"
              title="Indication"
              filterOptions={filterOptionsData?.indication || []}
              selectedValues={columnFilters.indication}
              onFilterChange={(values) =>
                handleFilterChange('indication', values)
              }
              onClear={() => handleFilterClear('indication')}
            />
          </span>
        ),
        dataIndex: 'indication',
        key: 'indication',
        sorter: true,
        render: (value: string | null, record) =>
          renderSampleLink(
            value ? (
              <span
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
              />
            ) : (
              '-'
            ),
            record,
          ),
      },
      {
        title: (
          <span>
            Reporter
            <FilterDropdown
              columnKey="reporter_display_name"
              title="Reporter"
              filterOptions={filterOptionsData?.reporter_display_name || []}
              selectedValues={columnFilters.reporter_display_name}
              onFilterChange={(values) =>
                handleFilterChange('reporter_display_name', values)
              }
              onClear={() => handleFilterClear('reporter_display_name')}
            />
          </span>
        ),
        key: 'reporter_display_name',
        sorter: true,
        render: (_, record) =>
          renderSampleLink(record.reporter?.display_name || '-', record),
      },
      {
        title: 'Entry date',
        dataIndex: 'received',
        key: 'received',
        sorter: true,
        render: (text, record) => renderSampleLink(text, record),
      },
      {
        title: 'Variants selected',
        dataIndex: 'variant_count',
        key: 'variant_count',
        width: 120,
        render: (_, record) => (
          <LinkedVariantsTooltip
            variants={record.genotype_variants || []}
            count={record.variant_count}
            showLinks={true}
          />
        ),
      },
      {
        title: '',
        key: 'claim',
        width: 110,
        render: (_, record) => (
          <Button
            type="primary"
            loading={isClaiming}
            onClick={(event) => {
              event.stopPropagation();
              void handleClaim(record);
            }}
          >
            Claim
          </Button>
        ),
      },
    ],
    [
      columnFilters,
      filterOptionsData,
      handleClaim,
      handleFilterChange,
      handleFilterClear,
      isClaiming,
    ],
  );

  return (
    <Table
      size="large"
      rowKey="id"
      columns={columns}
      dataSource={samples}
      loading={isFetching}
      onChange={handleTableChange}
      pagination={{
        current: currentPage,
        total: totalResultCount,
        pageSize,
        showSizeChanger: false,
        showTotal: (total) => `Total ${total} samples`,
      }}
      locale={{ emptyText: <Empty description="No samples in pool" /> }}
    />
  );
};

export default PoolTable;
